import { useState, useEffect, Fragment } from "react";
import { ReactComponent as Close } from "../../Assets/images/close.svg";
import { getRelationships } from "../../Services/Realtionship";
import { getShareArisGrants } from "../../Services/shareAris";
import {
  getUserSubscriptionDetails,
  billingPortal,
} from "../../Services/subscription";
import { getFaithProfile } from "../../Services/faithProfile";
import {
  getConversationLanguage,
  setConversationLanguage,
} from "../../Services/conversationLanguage";
import {
  getNotificationSettings,
  updateNotificationSettings,
} from "../../Services/userSettings";
import { useHeader } from "../../Providers/HeaderContextProvider";
import { isOnboardingComplete } from "../../Utils/onboardingNavigation";
import { useFunctionalityAccess } from "../../Providers/FunctionalityAccessProvider";
import { SettingsCard, SettingsRow } from "../SettingsList";
import { buildMenuTree, buildOnboardingMenuTree, SCREENS } from "./menuConfig";

/**
 * Header menu panel.
 *
 * Renders the navigation tree from menuConfig as an accordion: sections with
 * children expand in place, so the whole menu stays visible in one view. Rows
 * without children navigate straight to their page and carry no chevron.
 * See docs/navigation-redesign-plan.md §3.
 */
function MenuComponent({
  close,
  closeMenu,
  setShowLogoutPopup,
  hideRelationShipManager,
}) {
  const { profileContent } = useHeader();
  const {
    hasFeature,
    hasFaithAccess,
    hasUnlimitedCoaching,
    getCoachingMinutes,
    initialized: accessInitialized,
  } = useFunctionalityAccess();

  // Accordion: sections expand in place so the whole menu stays visible in one
  // view. One open section at a time keeps the panel from outgrowing the
  // viewport — swap for a Set if simultaneous sections are ever wanted.
  const [expandedSection, setExpandedSection] = useState(null);

  const [relationships, setRelationships] = useState([]);
  const [relationshipsLoaded, setRelationshipsLoaded] = useState(false);
  const [grants, setGrants] = useState([]);
  const [grantsLoaded, setGrantsLoaded] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoaded, setSubscriptionLoaded] = useState(false);
  const [faith, setFaith] = useState(null);
  const [faithLoaded, setFaithLoaded] = useState(false);
  const [notifications, setNotifications] = useState(null);
  const [notificationsLoaded, setNotificationsLoaded] = useState(false);
  const [language, setLanguage] = useState(null);
  const [languageLoaded, setLanguageLoaded] = useState(false);

  // Navigate without a full reload. history.pushState + a synthetic popstate
  // keeps React Router in sync while leaving the realtime websocket alive, so
  // leaving a coaching session mid-flight does not tear down audio.
  // Do not swap this for useNavigate.
  const navigateTo = (path) => {
    if (window.stopCoachingAudio) {
      window.stopCoachingAudio();
    }
    window.history.pushState({}, "", path);
    window.dispatchEvent(new PopStateEvent("popstate"));
    close?.();
  };

  useEffect(() => {
    const loadRelationships = async () => {
      try {
        setRelationships(await getRelationships());
      } catch (error) {
        console.error("Failed to fetch relationships", error);
      } finally {
        setRelationshipsLoaded(true);
      }
    };

    const loadShareAris = async () => {
      try {
        const { grants: loaded } = await getShareArisGrants();
        setGrants(loaded || []);
      } catch (error) {
        console.error("Failed to fetch Share Aris grants", error);
      } finally {
        setGrantsLoaded(true);
      }
    };

    loadRelationships();
    loadShareAris();
  }, []);

  // Section data is fetched when that section is expanded, not on menu open, so
  // opening the menu costs one round-trip rather than five.
  useEffect(() => {
    let cancelled = false;

    const load = async (alreadyLoaded, fetcher, apply, markLoaded, label) => {
      if (alreadyLoaded) return;
      try {
        const data = await fetcher();
        if (!cancelled) apply(data);
      } catch (error) {
        console.error(`Failed to fetch ${label}`, error);
      } finally {
        if (!cancelled) markLoaded(true);
      }
    };

    if (expandedSection === "membership") {
      load(
        subscriptionLoaded,
        getUserSubscriptionDetails,
        (data) => setSubscription(data || null),
        setSubscriptionLoaded,
        "subscription details"
      );
    }

    if (expandedSection === "faith") {
      // aris-ai's faith profile is the owner (a change made by talking to Aris lands
      // there first); the old /api/faith/me read a cached mirror that could lag.
      load(
        faithLoaded,
        getFaithProfile,
        (data) => {
          const profile = data?.data?.profile || null;
          setFaith(
            profile
              ? {
                  enabled: profile.enabled !== false,
                  faith: profile.enabled !== false ? profile.faith_tradition : null,
                  level: profile.enabled !== false ? profile.integration_depth : null,
                }
              : null
          );
        },
        setFaithLoaded,
        "faith profile"
      );
    }

    if (expandedSection === "voiceLanguage") {
      load(
        languageLoaded,
        getConversationLanguage,
        (data) => setLanguage(data || null),
        setLanguageLoaded,
        "default language"
      );
    }

    if (expandedSection === "notifications") {
      load(
        notificationsLoaded,
        getNotificationSettings,
        (data) => setNotifications(data || null),
        setNotificationsLoaded,
        "notification settings"
      );
    }

    return () => {
      cancelled = true;
    };
  }, [
    expandedSection,
    subscriptionLoaded,
    faithLoaded,
    notificationsLoaded,
    languageLoaded,
  ]);

  // Onboarding completion, from the header context or the persisted profile
  // (localStorage reflects a fresh redirect before the context catches up).
  const getCompletedStepsFromProfile = (p) =>
    p?.onboarding_step ?? p?.completed_steps ?? p?.completedSteps ?? [];

  const [onboardingDoneLocal, setOnboardingDoneLocal] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("profile") || "null");
      return isOnboardingComplete(getCompletedStepsFromProfile(stored || {}));
    } catch {
      return false;
    }
  });

  const onboardingDone =
    isOnboardingComplete(getCompletedStepsFromProfile(profileContent || {})) ||
    onboardingDoneLocal;

  useEffect(() => {
    const updateFromLocal = () => {
      try {
        const stored = JSON.parse(localStorage.getItem("profile") || "null");
        setOnboardingDoneLocal(
          isOnboardingComplete(getCompletedStepsFromProfile(stored || {}))
        );
      } catch {
        /* no-op */
      }
    };
    const onStorage = (e) => {
      if (e.key === "profile") updateFromLocal();
    };
    window.addEventListener("popstate", updateFromLocal);
    window.addEventListener("storage", onStorage);
    updateFromLocal();
    return () => {
      window.removeEventListener("popstate", updateFromLocal);
      window.removeEventListener("storage", onStorage);
    };
  }, [profileContent]);

  // Payload and response handling mirror handleBillingPortal in
  // ManageMembershipNew.jsx:307-330. Falls back to the membership page so a
  // portal failure still leaves the member somewhere useful.
  const openBillingPortal = async () => {
    try {
      const response = await billingPortal({
        return_url: `${window.location.origin}/manageMembership`,
        type: "payment_method",
        payload: {},
      });
      const url = response?.payload?.url || response?.url;
      if (url) {
        window.location.href = url;
        return;
      }
      navigateTo("/manageMembership");
    } catch (error) {
      console.error("Failed to open billing portal", error);
      navigateTo("/manageMembership");
    }
  };

  // Optimistic write with rollback: each toggle is its own PUT so one failing
  // field cannot discard another's change (plan §5.3).
  const setNotification = async (field, next) => {
    const previous = notifications;
    setNotifications((current) => ({ ...(current || {}), [field]: next }));
    try {
      const updated = await updateNotificationSettings({ [field]: next });
      if (updated) setNotifications(updated);
    } catch (error) {
      console.error(`Failed to update notification setting "${field}"`, error);
      setNotifications(previous);
    }
  };

  const ctx = {
    profile: profileContent,
    relationships,
    relationshipsLoaded,
    grants,
    grantsLoaded,
    subscription,
    subscriptionLoaded,
    faith,
    faithLoaded,
    language,
    languageLoaded,
    chooseLanguage: async (value) => {
      try {
        await setConversationLanguage(value);
        setLanguageLoaded(false); // re-read the row
      } catch (e) {
        console.error("Failed to update the default language", e);
      }
    },
    notifications,
    notificationsLoaded,
    setNotification,
    hasFaithAccess: accessInitialized ? hasFaithAccess() : false,
    accessLoaded: accessInitialized,
    coachingMinutes: accessInitialized ? getCoachingMinutes() : null,
    unlimitedCoaching: accessInitialized ? hasUnlimitedCoaching() : false,
    openLogout: () => {
      close?.();
      setShowLogoutPopup?.(true);
    },
    openBillingPortal,
  };

  const tree = onboardingDone ? buildMenuTree(ctx) : buildOnboardingMenuTree(ctx);

  // Show every row until access initialises, so the menu does not flicker.
  const isVisible = (row) => {
    if (row.id === "my-relationships" && hideRelationShipManager) return false;
    if (!row.requiredFeature) return true;
    if (!accessInitialized) return true;
    return hasFeature(row.requiredFeature);
  };

  const handleRow = (row) => {
    if (row.disabled) return;
    if (row.screen) {
      setExpandedSection((current) => (current === row.screen ? null : row.screen));
      return;
    }
    if (row.onClick) {
      row.onClick();
      return;
    }
    if (row.to) navigateTo(row.to);
  };

  // A chevron is reserved for rows that expand. Rows that go straight to a page
  // get no trailing affordance, so the icon never promises a level that is not
  // there.
  const affordanceFor = (row) => {
    if (row.right === "toggle") return "toggle";
    if (row.screen) return "expand";
    return "none";
  };

  const renderRow = (row, { nested = false } = {}) => (
    <SettingsRow
      key={row.id}
      icon={row.icon}
      label={row.label}
      value={row.value}
      badge={row.badge}
      right={affordanceFor(row)}
      expanded={!!row.screen && expandedSection === row.screen}
      nested={nested}
      danger={row.danger}
      disabled={row.disabled}
      checked={row.checked}
      onChange={row.onChange}
      onClick={() => handleRow(row)}
    />
  );

  // Children render inline beneath their parent — the accordion keeps the whole
  // menu in one view instead of replacing it with a sub-screen.
  const renderRows = (rows) =>
    rows.filter(isVisible).map((row) => {
      const isOpen = row.screen && expandedSection === row.screen;
      const children = isOpen ? SCREENS[row.screen].build(ctx) : [];

      return (
        <Fragment key={row.id}>
          {renderRow(row)}
          {children
            .filter(isVisible)
            .map((child) => renderRow(child, { nested: true }))}
        </Fragment>
      );
    });

  const initials = `${profileContent?.first_name?.[0]?.toUpperCase() || ""}${
    profileContent?.last_name?.[0]?.toUpperCase() || ""
  }`;

  const identityOpen =
    tree.identity && expandedSection === tree.identity.screen;

  return (
    // The panel lives inside the hamburger button, whose onClick toggles the
    // menu. Without this the first click on any row would close the panel and
    // expanding a section could never render.
    <div
      className="aris-settings-list aris-settings-list--on-dark menu-hero-container"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="header-option-close-icon" onClick={closeMenu}>
        <Close />
      </div>

      {tree.identity && (
        <SettingsCard>
          <SettingsRow
            icon={
              profileContent?.profile_pic_url ? (
                <img
                  src={profileContent.profile_pic_url}
                  alt=""
                  className="menu-identity-avatar"
                />
              ) : (
                <div className="menu-identity-avatar header-profile-circle-image">
                  {initials}
                </div>
              )
            }
            label={tree.identity.label}
            value={tree.identity.value}
            right={affordanceFor(tree.identity)}
            expanded={!!identityOpen}
            onClick={() => handleRow(tree.identity)}
            className="menu-identity-row"
          />
          {identityOpen &&
            SCREENS[tree.identity.screen]
              .build(ctx)
              .map((child) => renderRow(child, { nested: true }))}
        </SettingsCard>
      )}

      {tree.primary.length > 0 && (
        <SettingsCard>{renderRows(tree.primary)}</SettingsCard>
      )}
      <SettingsCard>{renderRows(tree.legal)}</SettingsCard>
      <SettingsCard>{renderRows(tree.danger)}</SettingsCard>
    </div>
  );
}

export default MenuComponent;
